import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRunStats } from "~/api/queries";
import { formatDuration } from "~/features/automation/format";
import { RUN_STATUS_COLORS, runKindLabel } from "~/features/automation/status";
import { Spinner } from "~/foundation/components/Spinner";
import { Text } from "~/foundation/components/Text";
import { useTheme } from "~/foundation/theme/useTheme";
import { cn } from "~/foundation/utils/cn";

/**
 * The Run Center's analytics band. Fed ONLY by `runs.stats` — no client-side aggregation
 * over the loaded run-list page, so the numbers always match what the server computed over
 * the full retention-capped history (§A.4). Self-contained: its own poll, independent of the
 * run list's.
 *
 * No chart library — the 14-day chart is hand-rolled inline SVG per the dataviz skill's
 * method (form → color-by-job → validated palette → marks → hover). The status colors reuse
 * `RUN_STATUS_COLORS` so the chart reads consistently with every status pill in the app; the
 * kind breakdown's 3-color set is the wave record's own validated categorical default — the
 * only slice of a bigger ramp that clears the all-pairs CVD gate in both light and dark.
 */

const COLOR_OTHER = "#8a8f98";

const KIND_COLORS_LIGHT: Record<string, string> = {
  digest: "#2a78d6",
  flow: "#eb6834",
  "pulse-advisor": "#1baf7a",
};
const KIND_COLORS_DARK: Record<string, string> = {
  digest: "#3987e5",
  flow: "#d95926",
  "pulse-advisor": "#199e70",
};

const STATUS_ORDER = [
  "requested",
  "running",
  "observing",
  "completed",
  // Its own bar, deliberately: folded into `completed` it would be invisible, and "how often
  // do our flows answer without their data" is the number this whole band exists to make
  // askable (§A.4).
  "completed-degraded",
  "failed",
  "cancelled",
  "paused",
];

const KNOWN_KINDS = ["digest", "flow", "pulse-advisor"];

function formatCompact(n: number): string {
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  const trim = (v: string) => (v.endsWith(".0") ? v.slice(0, -2) : v);
  if (abs < 1000) {
    return `${sign}${abs}`;
  }
  if (abs < 1_000_000) {
    return `${sign}${trim((abs / 1000).toFixed(1))}K`;
  }
  return `${sign}${trim((abs / 1_000_000).toFixed(1))}M`;
}

function formatPercent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function shortDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    return isoDate;
  }
  return d.toLocaleDateString(undefined, { month: "numeric", day: "numeric", timeZone: "UTC" });
}

function fullDayLabel(isoDate: string): string {
  const d = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) {
    return isoDate;
  }
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/** Rounded-top / square-baseline bar silhouette — one clip path per bar, so any number of
 * stacked, contiguous fills inherit the same outer shape. */
function roundedTopBarPath(
  x: number,
  yTop: number,
  width: number,
  height: number,
  r: number,
): string {
  const radius = Math.max(0, Math.min(r, width / 2, height));
  const yBottom = yTop + height;
  if (radius <= 0.01) {
    return `M${x},${yBottom} L${x},${yTop} L${x + width},${yTop} L${x + width},${yBottom} Z`;
  }
  return [
    `M${x},${yBottom}`,
    `L${x},${yTop + radius}`,
    `A${radius},${radius} 0 0 1 ${x + radius},${yTop}`,
    `L${x + width - radius},${yTop}`,
    `A${radius},${radius} 0 0 1 ${x + width},${yTop + radius}`,
    `L${x + width},${yBottom}`,
    "Z",
  ].join(" ");
}

function Metric({
  label,
  value,
  hint,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  color?: string;
}) {
  return (
    <div className="rounded-md border border-divider bg-bg-secondary px-4 py-3">
      <div className="mb-1 text-small font-medium text-text-tertiary">{label}</div>
      <div className="text-h2 font-semibold" style={color ? { color } : undefined}>
        {value}
      </div>
      {hint ? <div className="mt-0.5 text-small text-text-tertiary">{hint}</div> : null}
    </div>
  );
}

interface BreakdownRow {
  key: string;
  label: string;
  count: number;
  color: string;
}

function BreakdownBars({ rows }: { rows: BreakdownRow[] }) {
  const max = Math.max(1, ...rows.map((row) => row.count));
  return (
    <div className="flex flex-col gap-2">
      {rows.map((row) => (
        <div key={row.key} className="grid grid-cols-[84px_1fr_32px] items-center gap-2">
          <span className="truncate text-small text-text-secondary capitalize">{row.label}</span>
          <div className="h-2.5 overflow-hidden rounded-sm bg-bg">
            <div
              className="h-full min-w-[3px] rounded-e-sm"
              style={{ width: `${(row.count / max) * 100}%`, background: row.color }}
            />
          </div>
          <span className="text-end text-small text-text-tertiary tabular-nums">{row.count}</span>
        </div>
      ))}
    </div>
  );
}

const CHART_DAYS_HEADROOM = 10;
const CHART_BASELINE = 100;
const SLOT = 10;
const BAR_W = 6;

function DailyChart({
  daily,
  dark,
}: {
  daily: Array<{ date: string; total: number; completed: number; failed: number }>;
  dark: boolean;
}) {
  const { t } = useTranslation();
  const clipPrefix = useId();
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const gridline = dark ? "#2c2c2a" : "#e1e0d9";
  const baseline = dark ? "#383835" : "#c3c2b7";
  const hoverWash = dark ? "rgba(255,255,255,0.06)" : "rgba(11,11,11,0.05)";

  const dayMax = Math.max(1, ...daily.map((d) => d.total));
  const unitsPerCount = (CHART_BASELINE - CHART_DAYS_HEADROOM) / dayMax;
  const viewBoxWidth = daily.length * SLOT;
  const hovered = hoverIndex !== null ? daily[hoverIndex] : undefined;

  return (
    // SVG geometry ignores `dir`; this island stays LTR so the bars run oldest→newest and the
    // axis labels underneath (a plain row that WOULD reverse under RTL) agree with them.
    <div dir="ltr" className="flex flex-col">
      <div className="relative h-40 w-full">
        <svg
          viewBox={`0 0 ${viewBoxWidth} ${CHART_BASELINE}`}
          preserveAspectRatio="none"
          role="group"
          aria-label={t("Runs per day, last 14 days")}
          className="block h-full w-full [&_g:focus]:outline-none"
        >
          {[0, 0.5, 1].map((frac) => (
            <line
              key={frac}
              x1={0}
              x2={viewBoxWidth}
              y1={CHART_BASELINE - frac * (CHART_BASELINE - CHART_DAYS_HEADROOM)}
              y2={CHART_BASELINE - frac * (CHART_BASELINE - CHART_DAYS_HEADROOM)}
              stroke={gridline}
              strokeWidth={0.4}
            />
          ))}
          {daily.map((d, i) => {
            const other = Math.max(0, d.total - d.completed - d.failed);
            const completedH = d.completed * unitsPerCount;
            const otherH = other * unitsPerCount;
            const failedH = d.failed * unitsPerCount;
            const stackH = completedH + otherH + failedH;
            const x = i * SLOT + (SLOT - BAR_W) / 2;
            const yTop = CHART_BASELINE - stackH;
            const clipId = `${clipPrefix}-${i}`;
            const completedY = CHART_BASELINE - completedH;
            const otherY = completedY - otherH;
            const failedY = otherY - failedH;

            return (
              // biome-ignore lint/a11y/noStaticElementInteractions: an accessible chart data point — focusable + aria-label is the WAI-ARIA graphics pattern; there is no interactive host element to move the hover/focus handlers onto without losing the SVG geometry.
              <g
                key={d.date}
                tabIndex={0}
                aria-label={t(
                  "{{date}}: {{total}} runs, {{completed}} completed, {{failed}} failed",
                  {
                    date: fullDayLabel(d.date),
                    total: d.total,
                    completed: d.completed,
                    failed: d.failed,
                  },
                )}
                onMouseEnter={() => setHoverIndex(i)}
                onMouseLeave={() => setHoverIndex((h) => (h === i ? null : h))}
                onFocus={() => setHoverIndex(i)}
                onBlur={() => setHoverIndex((h) => (h === i ? null : h))}
              >
                <rect x={i * SLOT} y={0} width={SLOT} height={CHART_BASELINE} fill="transparent" />
                {stackH > 0 ? (
                  <>
                    <clipPath id={clipId}>
                      <path d={roundedTopBarPath(x, yTop, BAR_W, stackH, 1.5)} />
                    </clipPath>
                    <g clipPath={`url(#${clipId})`}>
                      {completedH > 0 ? (
                        <rect
                          x={x}
                          y={completedY}
                          width={BAR_W}
                          height={completedH}
                          fill={RUN_STATUS_COLORS.completed}
                        />
                      ) : null}
                      {otherH > 0 ? (
                        <rect x={x} y={otherY} width={BAR_W} height={otherH} fill={COLOR_OTHER} />
                      ) : null}
                      {failedH > 0 ? (
                        <rect
                          x={x}
                          y={failedY}
                          width={BAR_W}
                          height={failedH}
                          fill={RUN_STATUS_COLORS.failed}
                        />
                      ) : null}
                    </g>
                  </>
                ) : (
                  <rect x={x} y={CHART_BASELINE - 1} width={BAR_W} height={1} fill={baseline} />
                )}
                {hoverIndex === i ? (
                  <rect
                    x={x - 0.4}
                    y={0}
                    width={BAR_W + 0.8}
                    height={CHART_BASELINE}
                    fill={hoverWash}
                  />
                ) : null}
              </g>
            );
          })}
        </svg>
        {hoverIndex !== null && hovered ? (
          <div
            className="slate-pop pointer-events-none absolute bottom-[calc(100%+6px)] min-w-[140px] -translate-x-1/2 rounded-md border border-divider bg-menu-bg p-2 text-small shadow-menu"
            style={{ insetInlineStart: `${((hoverIndex + 0.5) / daily.length) * 100}%` }}
          >
            <div className="mb-1 font-semibold text-text">{fullDayLabel(hovered.date)}</div>
            <div className="flex items-center gap-1.5 text-text-secondary">
              <span
                className="size-2 shrink-0 rounded-xs"
                style={{ background: RUN_STATUS_COLORS.completed }}
                aria-hidden
              />
              {t("Completed")}
              <span className="ms-auto tabular-nums text-text">{hovered.completed}</span>
            </div>
            <div className="flex items-center gap-1.5 text-text-secondary">
              <span
                className="size-2 shrink-0 rounded-xs"
                style={{ background: RUN_STATUS_COLORS.failed }}
                aria-hidden
              />
              {t("Failed")}
              <span className="ms-auto tabular-nums text-text">{hovered.failed}</span>
            </div>
            <div className="flex items-center gap-1.5 text-text-secondary">
              <span
                className="size-2 shrink-0 rounded-xs"
                style={{ background: COLOR_OTHER }}
                aria-hidden
              />
              {t("Other")}
              <span className="ms-auto tabular-nums text-text">
                {Math.max(0, hovered.total - hovered.completed - hovered.failed)}
              </span>
            </div>
            <div className="mt-1.5 border-t border-divider pt-1.5 text-text-secondary">
              {t("Total")} <strong className="text-text">{hovered.total}</strong>
            </div>
          </div>
        ) : null}
      </div>
      <div className="mt-1 flex">
        {daily.map((d, i) => (
          <span key={d.date} className="flex-1 text-center text-small text-text-tertiary">
            {i === 0 || i === daily.length - 1 || i % 2 === 0 ? shortDayLabel(d.date) : ""}
          </span>
        ))}
      </div>
      <div className="mt-2.5 flex gap-4">
        <div className="flex items-center gap-1.5 text-small text-text-secondary">
          <span
            className="size-2 rounded-xs"
            style={{ background: RUN_STATUS_COLORS.completed }}
            aria-hidden
          />
          {t("Completed")}
        </div>
        <div className="flex items-center gap-1.5 text-small text-text-secondary">
          <span
            className="size-2 rounded-xs"
            style={{ background: RUN_STATUS_COLORS.failed }}
            aria-hidden
          />
          {t("Failed")}
        </div>
        <div className="flex items-center gap-1.5 text-small text-text-secondary">
          <span className="size-2 rounded-xs" style={{ background: COLOR_OTHER }} aria-hidden />
          {t("Other")}
        </div>
      </div>
    </div>
  );
}

export function RunAnalyticsBand() {
  const { t } = useTranslation();
  const { data: stats, isLoading } = useRunStats();
  const { resolved } = useTheme();
  const dark = resolved === "dark";
  const kindColors = dark ? KIND_COLORS_DARK : KIND_COLORS_LIGHT;

  if (isLoading || !stats) {
    return (
      <div className="flex flex-col gap-2">
        <h3 className="text-body font-medium text-text">{t("Analytics")}</h3>
        {isLoading ? (
          <Spinner label={t("Loading analytics…")} />
        ) : (
          <Text variant="small">{t("No analytics yet.")}</Text>
        )}
      </div>
    );
  }

  const statusRows: BreakdownRow[] = STATUS_ORDER.filter((status) => status in stats.byStatus).map(
    (status) => ({
      key: status,
      label: t(status),
      count: stats.byStatus[status] ?? 0,
      color: RUN_STATUS_COLORS[status] ?? COLOR_OTHER,
    }),
  );

  const kindRows: BreakdownRow[] = KNOWN_KINDS.map((kind) => ({
    key: kind,
    label: runKindLabel(kind, t),
    count: stats.byKind[kind] ?? 0,
    color: kindColors[kind] ?? COLOR_OTHER,
  }));

  const runs24h = stats.daily.length > 0 ? (stats.daily[stats.daily.length - 1]?.total ?? 0) : 0;
  const runs7d = stats.daily.slice(-7).reduce((sum, day) => sum + day.total, 0);

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-body font-medium text-text">{t("Analytics")}</h3>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <Metric label={t("Runs (24h)")} value={formatCompact(runs24h)} />
        <Metric label={t("Runs (7d)")} value={formatCompact(runs7d)} />
        <Metric
          label={t("Success rate")}
          value={formatPercent(stats.successRate)}
          color={RUN_STATUS_COLORS.completed}
          hint={t("{{count}} terminal runs", { count: stats.terminalRuns })}
        />
        <Metric
          label={t("Avg duration")}
          value={formatDuration(stats.avgDurationMs, t)}
          hint={t("{{count}} sampled", { count: stats.sampledRuns })}
        />
        <Metric
          label={t("Total tokens")}
          value={formatCompact(stats.tokens.total)}
          hint={t("{{in}} in · {{out}} out", {
            in: formatCompact(stats.tokens.input),
            out: formatCompact(stats.tokens.output),
          })}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-md border border-divider bg-bg-secondary p-4">
          <h4 className="mb-3 text-small font-semibold text-text-secondary">{t("By status")}</h4>
          <BreakdownBars rows={statusRows} />
        </div>
        <div className="rounded-md border border-divider bg-bg-secondary p-4">
          <h4 className="mb-3 text-small font-semibold text-text-secondary">{t("By kind")}</h4>
          <BreakdownBars rows={kindRows} />
        </div>
      </div>

      <div className="rounded-md border border-divider bg-bg-secondary p-4">
        <h4 className="mb-3 text-small font-semibold text-text-secondary">
          {t("Runs per day (14 days)")}
        </h4>
        <DailyChart daily={stats.daily} dark={dark} />
      </div>

      <div className="rounded-md border border-divider bg-bg-secondary p-4">
        <h4 className="mb-3 text-small font-semibold text-text-secondary">{t("By flow")}</h4>
        {stats.byName.length === 0 ? (
          <Text variant="small">{t("No runs in this window.")}</Text>
        ) : (
          <div className={cn("overflow-x-auto")}>
            <table className="w-full text-start text-small">
              <thead>
                <tr className="border-b border-divider text-text-tertiary">
                  <th className="px-2 py-1.5 text-start">{t("Flow")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Runs")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Success")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Avg duration")}</th>
                  <th className="px-2 py-1.5 text-end">{t("Tokens")}</th>
                </tr>
              </thead>
              <tbody>
                {stats.byName.map((row) => (
                  <tr key={row.name} className="border-b border-divider last:border-b-0">
                    <td className="px-2 py-1.5 font-semibold text-text">{row.name}</td>
                    <td className="px-2 py-1.5 text-end tabular-nums">{row.count}</td>
                    <td className="px-2 py-1.5 text-end tabular-nums">
                      {formatPercent(row.successRate)}
                    </td>
                    <td className="px-2 py-1.5 text-end tabular-nums">
                      {formatDuration(row.avgDurationMs, t)}
                    </td>
                    <td className="px-2 py-1.5 text-end tabular-nums">
                      {formatCompact(row.tokens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
